import React, { useState, useEffect, useRef } from 'react';
import DomeGallery from './DomeGallery';
import './Client.css';

const IMAGES = [
  '/images/Customer_logo/ampa-skyone.png',
  '/images/Customer_logo/2.jpg',
  '/images/Customer_logo/3.png',
  '/images/Customer_logo/4.jpg',
  '/images/Customer_logo/5.jpg',
  '/images/Customer_logo/7.png',
  '/images/Customer_logo/8.jpg',
  '/images/Customer_logo/9.gif',
  '/images/Customer_logo/jupiter_logo.png',
  '/images/Customer_logo/10.jpg',
  '/images/Customer_logo/11.JPG',
  '/images/Customer_logo/12.JPG',
  '/images/Customer_logo/13.JPG',
  '/images/Customer_logo/14.JPG',
  '/images/Customer_logo/15.JPG',
  '/images/Customer_logo/16.JPG',
  '/images/Customer_logo/17.jpg',
  '/images/Customer_logo/18.png',
  '/images/Customer_logo/19.jpg',
  '/images/Customer_logo/20.png',
  '/images/Customer_logo/22.jpg',
  '/images/Customer_logo/23.png',
  '/images/Customer_logo/24.png',
  '/images/Customer_logo/25.png',
  '/images/Customer_logo/26.jpg',
  '/images/Customer_logo/27.jpg',
  '/images/Customer_logo/28.png',
  '/images/Customer_logo/29.png',
  '/images/Customer_logo/30.jpg',
  '/images/Customer_logo/31.jpg',
  '/images/Customer_logo/32.jpg',
  '/images/Customer_logo/33.png',
  '/images/Customer_logo/34.jpg',
  '/images/Customer_logo/35.png',
  '/images/Customer_logo/36.jpg',
  '/images/Customer_logo/37.png',
  '/images/Customer_logo/38.jpg',
  '/images/Customer_logo/39.png',
  '/images/Customer_logo/rccl-logo.png',
  '/images/Customer_logo/melss-logo.png',
  '/images/Customer_logo/sarang.png',
  '/images/Customer_logo/indus.png',
  '/images/Customer_logo/ttdc-logo.png',
  '/images/Customer_logo/sbap-logo.png',
  '/images/Customer_logo/balaji-hatcheries-logo.png',
  '/images/clintimage/india-cements-logo.png',
  '/images/newcust/3.jpeg',
  '/images/newcust/4.jpg',
  '/images/newcust/5.png',
  '/images/newcust/6.jpeg',
  '/images/newcust/8.png',
  '/images/newcust/9.jpg',
];

// Division stages: how many cells visible at each stage
const STAGES = [1, 2, 4, 8, 16];

const Client = () => {
  // stage: 0 = 1 cell, 1 = 2 cells, 2 = 4 cells, 3 = 8, 4 = 16, 5 = full grid
  const [stage, setStage] = useState(0);
  const [splitting, setSplitting] = useState(false);
  const [fullGrid, setFullGrid] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [inView, setInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);          // grid-view network canvas
  const domeNetCanvasRef = useRef(null);  // dome 3D sphere network canvas
  const containerRef = useRef(null);
  const pulseOffsetRef = useRef(0);
  const timeoutsRef = useRef([]);


  useEffect(() => {
    // Trigger when section scrolls into view
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Set up background network node canvas animation once full grid is revealed
  useEffect(() => {
    if (!fullGrid || !isMobile) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const handleResize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const items = container.querySelectorAll('.gallery-item');
      if (items.length > 0) {
        const containerRect = container.getBoundingClientRect();
        
        // Calculate center coordinate for each card relative to container
        const nodes = Array.from(items).map((item) => {
          const itemRect = item.getBoundingClientRect();
          return {
            x: itemRect.left - containerRect.left + itemRect.width / 2,
            y: itemRect.top - containerRect.top + itemRect.height / 2,
          };
        });

        // 1. Draw organic network lines in the background
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(107, 122, 82, 0.04)';
        ctx.lineWidth = 1;
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dist = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
            if (dist < 260) {
              ctx.moveTo(nodes[i].x, nodes[i].y);
              ctx.lineTo(nodes[j].x, nodes[j].y);
            }
          }
        }
        ctx.stroke();

        // 2. Draw active connections as a single-shot pulse wave (fade-out ripple)
        if (hoveredIndex !== null && nodes[hoveredIndex] && pulseOffsetRef.current <= 1) {
          const startNode = nodes[hoveredIndex];

          // Increment offset once per frame
          pulseOffsetRef.current += 0.022; // Controls speed of the single broadcast wave

          // Linear fade-out multiplier based on signal progression
          const opacityMultiplier = Math.max(0, 1 - pulseOffsetRef.current);

          // Draw halo around hovered node
          ctx.beginPath();
          ctx.arc(startNode.x, startNode.y, 45 * pulseOffsetRef.current + 5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(107, 122, 82, ${0.12 * opacityMultiplier})`;
          ctx.fill();

          // Connect from hovered node to all other nodes
          ctx.beginPath();
          ctx.strokeStyle = `rgba(107, 122, 82, ${0.18 * opacityMultiplier})`;
          ctx.lineWidth = 1.5;
          nodes.forEach((endNode, idx) => {
            if (idx === hoveredIndex) return;
            ctx.moveTo(startNode.x, startNode.y);
            ctx.lineTo(endNode.x, endNode.y);
          });
          ctx.stroke();

          // Draw single-shot active pulse particles moving down the paths
          nodes.forEach((endNode, idx) => {
            if (idx === hoveredIndex) return;
            
            const t = Math.min(1, pulseOffsetRef.current);
            const px = startNode.x + (endNode.x - startNode.x) * t;
            const py = startNode.y + (endNode.y - startNode.y) * t;

            ctx.beginPath();
            ctx.arc(px, py, 3.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(107, 122, 82, ${opacityMultiplier})`;
            ctx.shadowColor = '#6b7a52';
            ctx.shadowBlur = 4 * opacityMultiplier;
            ctx.fill();
            ctx.shadowBlur = 0; // Reset shadow for other drawings
          });
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [fullGrid, isMobile, hoveredIndex]);

  // Reset pulse progress whenever a new card is hovered, initiating a fresh signal burst
  useEffect(() => {
    pulseOffsetRef.current = 0;
  }, [hoveredIndex]);

  // ── Dome 3D Sphere Network Canvas ──────────────────────────────────────────
  // Renders a rotating node-and-line network on the sphere surface, BEHIND logos
  useEffect(() => {
    if (isMobile) return;

    const canvas = domeNetCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animId;
    let rotY = 0; // current Y-rotation in radians

    // Track mouse coordinate for interactive synapse beams
    let mouse = { x: -1000, y: -1000, active: false };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };

    const handleMouseLeave = () => {
      mouse.active = false;
    };

    const parent = canvas.parentElement;
    if (parent) {
      parent.addEventListener('mousemove', handleMouseMove);
      parent.addEventListener('mouseleave', handleMouseLeave);
      // Support touches as well
      const handleTouchMove = (e) => {
        if (e.touches.length > 0) {
          const rect = canvas.getBoundingClientRect();
          mouse.x = e.touches[0].clientX - rect.left;
          mouse.y = e.touches[0].clientY - rect.top;
          mouse.active = true;
        }
      };
      parent.addEventListener('touchmove', handleTouchMove, { passive: true });
      parent.addEventListener('touchend', handleMouseLeave);
    }

    // Resize canvas to match wrapper pixel dimensions
    const setSize = () => {
      const parentEl = canvas.parentElement;
      if (!parentEl) return;
      const r = parentEl.getBoundingClientRect();
      canvas.width  = Math.round(r.width);
      canvas.height = Math.round(r.height);
    };
    setSize();
    const ro = new ResizeObserver(setSize);
    ro.observe(canvas.parentElement);

    // ── Fibonacci sphere node distribution ─────────────────────────────────
    const NUM_NODES = 120;
    const nodes = Array.from({ length: NUM_NODES }, (_, i) => {
      const phi   = Math.acos(1 - 2 * (i + 0.5) / NUM_NODES);   // polar
      const theta = 2 * Math.PI * ((1 + Math.sqrt(5)) / 2) * i; // golden angle
      return { phi, theta };
    });

    // ── Twinkling Cosmic Dust (floating ambient stars) ─────────────────────
    const DUST_COUNT = 40;
    const dust = Array.from({ length: DUST_COUNT }, () => ({
      x: Math.random(),
      y: Math.random(),
      size: 0.5 + Math.random() * 1.5,
      speed: 0.001 + Math.random() * 0.002,
      phase: Math.random() * Math.PI * 2,
    }));

    // ── Pulsing energy particles that travel along edges ──────────────────
    const particles = [];
    for (let i = 0; i < 15; i++) {
      particles.push({
        nodeA: Math.floor(Math.random() * NUM_NODES),
        nodeB: Math.floor(Math.random() * NUM_NODES),
        t:     Math.random(),        // 0→1 travel progress
        speed: 0.003 + Math.random() * 0.005,
      });
    }

    const BRAND_R = 107, BRAND_G = 122, BRAND_B = 82; // --brand sage green
    const rgba = (a) => `rgba(${BRAND_R},${BRAND_G},${BRAND_B},${a.toFixed(3)})`;

    const animate = () => {
      // Rotate at same speed as DomeGallery's autoRotate (−0.08 deg/frame)
      rotY -= 0.08 * (Math.PI / 180);

      const W  = canvas.width;
      const H  = canvas.height;
      const cx = W / 2;
      const cy = H / 2;

      // Sphere radius: match the visual globe
      const sphereR = Math.min(W, H) * 0.42;
      const MAX_DIST = sphereR * 0.45; // max connection distance

      ctx.clearRect(0, 0, W, H);

      // ── 1. Draw Twinkling Cosmic Dust in the background ───────────────────
      dust.forEach(d => {
        d.phase += d.speed;
        const opacity = 0.05 + Math.sin(d.phase) * 0.15;
        if (opacity > 0) {
          ctx.beginPath();
          ctx.arc(d.x * W, d.y * H, d.size, 0, Math.PI * 2);
          ctx.fillStyle = rgba(opacity);
          ctx.fill();
        }
      });

      // ── 2. Project nodes to screen space ──────────────────────────────────
      const pts = nodes.map(({ phi, theta }) => {
        const t  = theta + rotY;
        const x3 =  Math.sin(phi) * Math.cos(t);
        const y3 =  Math.cos(phi);
        const z3 =  Math.sin(phi) * Math.sin(t);
        const depth = (z3 + 1) / 2; // 0 = back, 1 = front
        return {
          sx: cx + sphereR * x3,
          sy: cy - sphereR * y3, // canvas Y is inverted
          z3,
          depth,
        };
      });

      // ── 3. Draw curved organic Bezier connections (front hemisphere only) ──
      ctx.lineWidth = 0.8;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        if (a.z3 < -0.05) continue;  // skip back hemisphere
        for (let j = i + 1; j < pts.length; j++) {
          const b = pts[j];
          if (b.z3 < -0.05) continue;
          const dx = a.sx - b.sx;
          const dy = a.sy - b.sy;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < MAX_DIST) {
            const proximityFade = 1 - d / MAX_DIST;
            const depthFade     = Math.min(a.depth, b.depth);
            const alpha         = proximityFade * depthFade * 0.28;

            // Calculate bend control point towards the center to create a nice spherical curve
            const mx = (a.sx + b.sx) / 2;
            const my = (a.sy + b.sy) / 2;
            const curveBend = 0.18; // Pull inward slightly
            const ctrlX = mx + (cx - mx) * curveBend;
            const ctrlY = my + (cy - my) * curveBend;

            // High-fidelity color gradients: transition front lines with butter gold highlight
            if (depthFade > 0.65) {
              const lineGrad = ctx.createLinearGradient(a.sx, a.sy, b.sx, b.sy);
              lineGrad.addColorStop(0, rgba(alpha * 0.7));
              lineGrad.addColorStop(0.5, `rgba(242, 217, 150, ${alpha * 1.1})`); // Butter gold accent #F2D996
              lineGrad.addColorStop(1, rgba(alpha * 0.7));
              ctx.strokeStyle = lineGrad;
            } else {
              ctx.strokeStyle = rgba(alpha);
            }

            ctx.beginPath();
            ctx.moveTo(a.sx, a.sy);
            ctx.quadraticCurveTo(ctrlX, ctrlY, b.sx, b.sy);
            ctx.stroke();
          }
        }
      }

      // ── 4. Draw interactive mouse-tethered synaptic glow-beams ────────────
      if (mouse.active) {
        const activePts = pts
          .map((p, idx) => ({ ...p, idx, dist: Math.hypot(p.sx - mouse.x, p.sy - mouse.y) }))
          .filter(p => p.z3 > 0.05) // Front facing nodes only
          .sort((a, b) => a.dist - b.dist)
          .slice(0, 5); // Connect to 5 closest nodes

        activePts.forEach(p => {
          const maxTetherDist = sphereR * 0.55;
          if (p.dist < maxTetherDist) {
            const opacityMultiplier = (1 - p.dist / maxTetherDist) * p.depth;
            const alpha = opacityMultiplier * 0.45;

            // Create a gorgeous gradient tether from cursor to node
            const tetherGrad = ctx.createLinearGradient(mouse.x, mouse.y, p.sx, p.sy);
            tetherGrad.addColorStop(0, `rgba(242, 217, 150, ${alpha * 1.5})`); // Vibrant buttercream gold
            tetherGrad.addColorStop(0.6, rgba(alpha * 0.9));
            tetherGrad.addColorStop(1, rgba(alpha * 0.4));

            ctx.strokeStyle = tetherGrad;
            ctx.lineWidth = 1.2;

            // Curved tether line
            const mx = (mouse.x + p.sx) / 2;
            const my = (mouse.y + p.sy) / 2;
            const ctrlX = mx + (cx - mx) * 0.1;
            const ctrlY = my + (cy - my) * 0.1;

            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.quadraticCurveTo(ctrlX, ctrlY, p.sx, p.sy);
            ctx.stroke();

            // Tiny light spark traveling along the tether to the node
            const t = (Date.now() / 900) % 1.0;
            const sx = mouse.x + (p.sx - mouse.x) * t;
            const sy = mouse.y + (p.sy - mouse.y) * t;

            ctx.beginPath();
            ctx.arc(sx, sy, 2.2, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(242, 217, 150, ${opacityMultiplier})`;
            ctx.shadowColor = '#F2D996';
            ctx.shadowBlur = 4 * opacityMultiplier;
            ctx.fill();
            ctx.shadowBlur = 0;
          }
        });
      }

      // ── 5. Draw node dots with glowing halos ──────────────────────────────
      for (const p of pts) {
        if (p.z3 < -0.05) continue;
        const r = 1.2 + p.depth * 2.2;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
        ctx.fillStyle = rgba(p.depth * 0.6);
        ctx.fill();

        // Elegant glow halo on front-facing nodes
        if (p.depth > 0.72) {
          ctx.beginPath();
          ctx.arc(p.sx, p.sy, r + 2.8, 0, Math.PI * 2);
          ctx.fillStyle = rgba((p.depth - 0.72) * 0.22);
          ctx.fill();
        }
      }

      // ── 6. Animate organic energy particles along curves ─────────────────
      for (const par of particles) {
        par.t += par.speed;
        if (par.t >= 1) {
          par.t     = 0;
          par.nodeA = par.nodeB;
          par.nodeB = Math.floor(Math.random() * NUM_NODES);
          par.speed = 0.003 + Math.random() * 0.005;
        }
        const a = pts[par.nodeA];
        const b = pts[par.nodeB];
        if (!a || !b || a.z3 < 0 || b.z3 < 0) continue;

        // Trace quadratic Bezier path rather than a straight line
        const mx = (a.sx + b.sx) / 2;
        const my = (a.sy + b.sy) / 2;
        const curveBend = 0.18;
        const ctrlX = mx + (cx - mx) * curveBend;
        const ctrlY = my + (cy - my) * curveBend;

        // Quadratic bezier interpolation formula
        const t = par.t;
        const px = (1 - t) * (1 - t) * a.sx + 2 * (1 - t) * t * ctrlX + t * t * b.sx;
        const py = (1 - t) * (1 - t) * a.sy + 2 * (1 - t) * t * ctrlY + t * t * b.sy;

        const depthAt = a.depth + (b.depth - a.depth) * par.t;
        if (depthAt < 0.3) continue;

        // Bright traveling particle spark with trails
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(242, 217, 150, ${(depthAt * 0.9).toFixed(2)})`; // butter gold sparkles
        ctx.shadowColor = '#F2D996';
        ctx.shadowBlur  = 6 * depthAt;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      if (parent) {
        parent.removeEventListener('mousemove', handleMouseMove);
        parent.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [isMobile]);

  const runDivisionSequence = () => {
    // Clear any existing timeouts first
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    // Stage timings: 1 → 2 → 4 → 8 → 16 → full grid
    const timings = [900, 1800, 2700, 3600, 4500, 5600];

    timings.forEach((delay, i) => {
      const t1 = setTimeout(() => {
        if (i < STAGES.length) {
          setSplitting(true);
          const t2 = setTimeout(() => {
            setStage(i + 1 < STAGES.length ? i + 1 : STAGES.length - 1);
            setSplitting(false);
          }, 400);
          timeoutsRef.current.push(t2);
        } else {
          // Final: transition to full grid
          setSplitting(true);
          const t3 = setTimeout(() => {
            setFullGrid(true);
            setSplitting(false);
          }, 500);
          timeoutsRef.current.push(t3);
        }
      }, delay);
      timeoutsRef.current.push(t1);
    });
  };

  // Manage cell division animation triggers specifically for mobile standard grid view
  useEffect(() => {
    if (isMobile) {
      if (inView && !fullGrid) {
        runDivisionSequence();
      }
    } else {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      setSplitting(false);
      setStage(0);
    }

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, [isMobile, inView, fullGrid]);

  // Get current number of cells based on stage
  const cellCount = STAGES[stage] || 1;
  // Pick evenly spaced images from the list for each stage
  const getStageImages = (count) => {
    const step = Math.floor(IMAGES.length / count);
    return Array.from({ length: count }, (_, i) => IMAGES[i * step] || IMAGES[i]);
  };

  return (
    <div className="client-section" ref={sectionRef}>
      <h1 className="client-header">Trusted by Industry Leaders</h1>
      <p className="client-description">
        Over the years, we have partnered with businesses across various sectors, delivering excellence through our tailored Tally and IT solutions.
      </p>

      {isMobile ? (
        /* ── MOBILE DEVICE: Standard Grid View Only ── */
        fullGrid ? (
          <div className="gallery-layout-wrapper">
            <div className="gallery-container full-grid-reveal" ref={containerRef}>
              <canvas ref={canvasRef} className="network-canvas" />
              {IMAGES.map((img, index) => (
                <div
                  key={index}
                  className="gallery-item"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <div className="flip-card-inner">
                    <div className="flip-card-front">
                      <img src={img} alt={`Customer logo ${index + 1}`} loading="lazy" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ── Biological Cell Division Stage (First Load Split) ── */
          <div className={`cell-arena ${splitting ? 'splitting' : ''}`}>
            <div
              className="cell-grid"
              data-count={cellCount}
            >
              {getStageImages(cellCount).map((img, i) => (
                <div
                  key={`${stage}-${i}`}
                  className="cell-card"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="cell-inner">
                    <img src={img} alt={`Customer ${i + 1}`} loading="lazy" />
                  </div>
                  {/* Split line animation */}
                  <div className="cell-split-h" />
                  <div className="cell-split-v" />
                </div>
              ))}
            </div>

            {/* Stage indicator */}
            <div className="stage-indicator">
              {STAGES.map((_, i) => (
                <div
                  key={i}
                  className={`stage-dot ${i <= stage ? 'reached' : ''} ${i === stage ? 'current' : ''}`}
                />
              ))}
              <div className="stage-dot final" />
            </div>

            <p className="division-label">
              {stage === 0 && 'Our first customer partner…'}
              {stage === 1 && 'Growing our network…'}
              {stage === 2 && 'Expanding rapidly…'}
              {stage === 3 && 'Trusted by many…'}
              {stage >= 4 && 'Meet our full family…'}
            </p>
          </div>
        )
      ) : (
        /* ── DESKTOP DEVICE: Interactive 3D Dome View Only ── */
        <div className="gallery-layout-wrapper">
          <div className="dome-gallery-wrapper">
            {/* 3D sphere network canvas — renders BEHIND the logo tiles */}
            <canvas ref={domeNetCanvasRef} className="dome-network-canvas" aria-hidden="true" />
            <DomeGallery 
              images={IMAGES} 
              grayscale={false} 
              fit={0.88} 
              fitBasis="width"
              minRadius={700}
              overlayBlurColor="#EBE7D9"
              openedImageWidth="320px" 
              openedImageHeight="320px" 
              imageBorderRadius="20px"
              openedImageBorderRadius="24px"
            />
          </div>
          {/* <p className="dome-hint">
            <span>↻</span> Drag to spin &nbsp;&bull;&nbsp; Click a logo to focus
          </p> */}
        </div>
      )}
    </div>
  );
};

export default Client;
